import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  InputBase,
  Typography,
  Box,
  Popover,
  FormControl,
  ListItemButton,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ErrorMessage } from "formik";

export interface CompanyOption {
  id: number;
  company_name: string;
}

export interface MultiSelectCompanyProps {
  options: CompanyOption[];
  fieldName?: string;
  isShowError?: boolean;
  handleBlur?: () => void;
  onChange?: (selected: (number | string)[] | null) => void;
  value?: (number | string)[];
  isOrder?: boolean;
}

const CompanyFilter: React.FC<MultiSelectCompanyProps> = ({
  options,
  fieldName,
  isShowError = false,
  handleBlur,
  onChange,
  value = [],
  isOrder = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isOrderBased = !!isOrder;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    handleBlur?.();
    setSearchText("");
  };

  useEffect(() => {
    if (anchorEl && inputRef.current) {
      inputRef.current.focus();
    }
  }, [anchorEl]);

  const handleToggle = (key: number | string) => {
    const newValue = value?.includes(key)
      ? value.filter((v) => v !== key)
      : [...value, key];
  
    if (isOrderBased) {
      onChange?.(newValue as (string | number)[]);
    } else {
      onChange?.((newValue.filter((v): v is number => typeof v === "number")) || null);
    }
  };

  const isSelected = useCallback(
    (key: number | string) => value.includes(key),
    [value]
  );

  const filteredOptions = useMemo(
    () =>
      options.filter((opt) =>
        opt.company_name.toLowerCase().includes(searchText.toLowerCase())
      ),
    [options, searchText]
  );

  return (
    <Box>
      <FormControl fullWidth error={isShowError}>
        <Box
          onClick={handleOpen}
          sx={{
            borderRadius: "4px",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            height: "18px",
            background: "#fff",
            border: "1px solid #ccc",
          }}
        >
          <Typography sx={{ color: value.length ? "#000" : isShowError ? "#e30000" : "#aaa" }}>
            {value.length
              ? options
                  .filter((o) =>
                    isOrderBased
                      ? value.includes(o.company_name)
                      : value.includes(o.id)
                  )
                  .map((o) => o.company_name)
                  .slice(0, 2)
                  .join(", ") + (value.length > 2 ? "..." : "")
              : "Select Company"}
          </Typography>
          <ExpandMoreIcon />
        </Box>

        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            style: {
              width: anchorEl?.clientWidth,
            },
          }}
        >
          <Box sx={{ padding: "8px", position: "relative" }}>
            <SearchIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "16px",
                transform: "translateY(-50%)",
                color: "#8D8D8D",
                pointerEvents: "none",
                fontSize: 20,
                zIndex: 1,
              }}
            />
            <InputBase
              inputRef={inputRef}
              placeholder="Search Company"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              sx={{
                padding: "8px 12px 8px 40px",
                borderRadius: "6px",
                backgroundColor: "#f5f5f5",
                fontSize: "14px",
                height: "37px",
                color: "#000",
              }}
            />
          </Box>

          <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const key = isOrderBased ? option.company_name : option.id;
              
                return (
                  <ListItemButton
                    key={key}
                    dense
                    disableGutters
                    onClick={() => handleToggle(key)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 16px",
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.company_name}
                    </Typography>
                    <Checkbox
                      checked={isSelected(key)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggle(key)}
                      sx={{
                        padding: 0,
                        "&.Mui-checked": {
                          color: "#1E3763",
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })
            ) : (
              <Typography sx={{ padding: "8px 16px", color: "#888" }}>
                No companies found
              </Typography>
            )}
          </Box>
        </Popover>
      </FormControl>

      {isShowError && fieldName && (
        <ErrorMessage
          name={fieldName}
          component="div"
          className="error-message"
        />
      )}
    </Box>
  );
};

export default CompanyFilter;
